import { stableStringify } from '../checklist/ChecklistSnapshotCreationService';
import { normalizeChecklistSnapshotHistoricalRow } from '../checklist/ChecklistSnapshotRemoteCreateService';
import { sameHistoricalIdentity, type HistoricalRow } from './HistoricalSnapshotHydrationService';

export interface ChecklistCreateAcknowledgementDependencies {
  enabled(companyId: string): Promise<boolean>;
  readLocal(): Promise<HistoricalRow | null>;
  readSnapshot(id: string, companyId: string, remote: boolean): Promise<HistoricalRow | null>;
  unchanged(): Promise<boolean>;
  archive(): Promise<void>;
  remove(): Promise<boolean>;
}

const comparable = (row: HistoricalRow): HistoricalRow => {
  const result = { ...row };
  for (const key of Object.keys(result)) {
    if (key.endsWith('_at') || key.startsWith('data_')) {
      const time = typeof result[key] === 'number' ? result[key] : Date.parse(result[key]);
      if (Number.isFinite(time)) result[key] = time;
    }
  }
  return result;
};

export class ChecklistCreateAcknowledgementService {
  constructor(private readonly deps: ChecklistCreateAcknowledgementDependencies) {}

  async process(payload: HistoricalRow, remote: HistoricalRow): Promise<boolean> {
    const identity = ['id', 'empresa_id', 'usuario_id', 'template_id', 'snapshot_id'];
    if (identity.some(key => !payload[key] || payload[key] !== remote[key])) return false;
    if (!await this.deps.enabled(payload.empresa_id)) return false;
    const local = await this.deps.readLocal();
    if (!local || identity.some(key => local[key] !== payload[key])) return false;
    const left = comparable(local);
    const right = comparable(remote);
    // O pai atual deve estar integralmente representado no servidor; não reaplicar o CREATE antigo.
    if (Object.keys(left).some(key => stableStringify(left[key] ?? null) !== stableStringify(right[key] ?? null))) return false;
    const localSnapshot = await this.deps.readSnapshot(payload.snapshot_id, payload.empresa_id, false);
    const remoteSnapshot = await this.deps.readSnapshot(payload.snapshot_id, payload.empresa_id, true);
    if (!localSnapshot || !remoteSnapshot || !sameHistoricalIdentity(local, localSnapshot)
      || !sameHistoricalIdentity(remote, remoteSnapshot)) return false;
    const normalizedLocal = normalizeChecklistSnapshotHistoricalRow('guincho_checklist_snapshots', localSnapshot);
    const normalizedRemote = normalizeChecklistSnapshotHistoricalRow('guincho_checklist_snapshots', remoteSnapshot);
    if (['template_schema_version_id', 'schema_hash', 'schema_payload'].some(key =>
      !normalizedLocal[key] || normalizedLocal[key] !== normalizedRemote[key])) return false;
    const currentStillRepresented = async () => {
      const current = await this.deps.readLocal();
      return current && stableStringify(comparable(current)) === stableStringify(left);
    };
    if (!await this.deps.unchanged() || !await currentStillRepresented()) return false;
    await this.deps.archive();
    if (!await this.deps.unchanged() || !await currentStillRepresented()) return false;
    return this.deps.remove();
  }
}
